/**
 * What a person asks when they open the records behind a mark, with no React in it.
 *
 * A bar stands for the records whose dimension values are the bar's, among the
 * records the chart's own filters let through. So the question is the chart's
 * filters plus one more per dimension, and it is asked as an `ids` spec: the
 * kernel gets ids from the copy, then reads each record itself and writes one
 * audit row before handing any back (`POST /insights/drill`).
 *
 * ## What this never does
 *
 * It never decides what a person may open. A spec carries filters and nothing
 * else, and what comes back is what the kernel read as the person, so a list can
 * be shorter than the bar it was opened from: the chart is a copy a few seconds
 * behind, and a record changed since is not shown. The dialog says so once and
 * does not say how many.
 */
import {
  NONE_LABEL,
  buildSpec,
  fieldsOf,
  humanise,
  kindOf,
} from "./spec.js";

/** The kernel allows eight filters in a spec, and a drill adds up to four. */
export const MAX_FILTERS = 8;

/** Most records a drill asks for; the kernel's own ceiling is 200. */
export const DRILL_LIMIT = 100;

const DAY = 86_400_000;

/**
 * The half-open range of time a bucket label stands for, in UTC, which is how
 * the query function cuts them. `2026-Q3` is July to the end of September;
 * a week starts on the Monday the label names.
 */
export function bucketRange(bucket, label) {
  const text = String(label ?? "").trim();
  let start;
  let end;
  let m;
  if (bucket === "year" && (m = /^(\d{4})$/.exec(text))) {
    const y = Number(m[1]);
    start = Date.UTC(y, 0, 1);
    end = Date.UTC(y + 1, 0, 1);
  } else if (bucket === "quarter" && (m = /^(\d{4})-Q([1-4])$/.exec(text))) {
    const y = Number(m[1]);
    const q = Number(m[2]);
    start = Date.UTC(y, (q - 1) * 3, 1);
    end = Date.UTC(y, q * 3, 1);
  } else if (bucket === "month" && (m = /^(\d{4})-(\d{2})$/.exec(text))) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    if (mo < 1 || mo > 12) return null;
    start = Date.UTC(y, mo - 1, 1);
    end = Date.UTC(y, mo, 1);
  } else if ((bucket === "day" || bucket === "week") && (m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text))) {
    start = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    end = start + (bucket === "week" ? 7 : 1) * DAY;
  } else {
    return null;
  }
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

/** The dimensions of a form that `buildSpec` actually sends, in order. */
export function activeDimensions(state, schema) {
  const by = new Set(fieldsOf(schema, state.type).map((f) => f.name));
  return state.dimensions.filter((d) => d.field && by.has(d.field))
    .map((d) => ({ field: d.field, bucket: d.bucket || "" }));
}

/**
 * The marks a chart point stands for: one `{ field, bucket, value }` per
 * dimension. The first is the row's own value; the second, when the chart split
 * its series by it, is the series' name.
 */
export function marksFromPoint(shaped, state, schema, { index, seriesKey }) {
  const dims = activeDimensions(state, schema);
  const row = shaped?.rows?.[index];
  if (!row || !shaped.dimension) return null;
  const marks = [];
  if (dims[0]) marks.push({ ...dims[0], value: row[shaped.dimension.key] });
  if (shaped.splitBy && dims[1]) {
    const series = shaped.series.find((s) => s.key === seriesKey);
    if (!series) return null;
    marks.push({ ...dims[1], value: series.label });
  }
  return marks;
}

/** The marks a row of a table stands for, read from its dimension columns. */
export function marksFromRow(shaped, state, schema, row) {
  const dims = activeDimensions(state, schema);
  const cols = (shaped?.columns || []).filter((c) => c.role === "dimension");
  return cols.map((c, i) => (dims[i] ? { ...dims[i], value: row[c.key] } : null)).filter(Boolean);
}

/**
 * The `ids` spec for a set of marks, or `{ ok: false, why }` when it cannot be
 * asked. Never throws: a chart that cannot be drilled says why, in words.
 */
export function drillSpec(state, schema, marks) {
  const fields = fieldsOf(schema, state.type);
  const by = Object.fromEntries(fields.map((f) => [f.name, f]));
  const filters = [...(buildSpec(state, schema).filters || [])];

  for (const mark of marks || []) {
    const field = by[mark.field];
    if (!field) return { ok: false, why: `${humanise(mark.field)} is not a field of ${state.type}.` };
    if (mark.value === NONE_LABEL) {
      filters.push({ field: mark.field, op: "is_not_set" });
    } else if (mark.bucket) {
      const range = bucketRange(mark.bucket, mark.value);
      if (!range) return { ok: false, why: `“${mark.value}” is not a ${mark.bucket} the records can be narrowed to.` };
      filters.push({ field: mark.field, op: "gte", value: range.from },
        { field: mark.field, op: "lt", value: range.to });
    } else if (kindOf(field) === "number") {
      const n = Number(mark.value);
      if (!Number.isFinite(n)) return { ok: false, why: `“${mark.value}” is not a number.` };
      filters.push({ field: mark.field, op: "eq", value: n });
    } else {
      filters.push({ field: mark.field, op: "eq", value: String(mark.value) });
    }
  }

  if (filters.length > MAX_FILTERS) {
    return {
      ok: false,
      why: `This chart already has ${filters.length - (marks || []).length} filters, and opening a mark adds more. `
        + `Take a filter off the chart to open its records.`,
    };
  }
  const spec = { type: state.type, mode: "ids", limit: DRILL_LIMIT };
  if (filters.length) spec.filters = filters;
  // A chart limited to linked records opens only those records (LO-227).
  if (state.source) spec.source = state.source;
  if (state.linked) spec.linked = state.linked;
  return { ok: true, spec };
}

/** "Status: PAID, Issued at: 2026-09" — what a mark is, for a dialog's title. */
export function describeMarks(marks) {
  return (marks || []).map((m) => `${humanise(m.field)}: ${m.value}`).join(", ");
}

/**
 * The columns a list of records shows: first the fields the mark is about, so a
 * list opened from the PAID bar has its status column, then the type's first
 * fields, none of them a list or an object and none counted-only. A name or an
 * invoice number is shown, because a person opening a record needs to know which
 * one it is; an email or a message's text is not, and is one press away in the
 * record's own details, where it is the record being read and not a column being
 * scanned.
 */
export function recordColumns(schema, typeName, limit = 6, first = []) {
  const shown = fieldsOf(schema, typeName)
    .filter((f) => !f.aggregate_only && kindOf(f) !== "composite");
  const lead = first.map((name) => shown.find((f) => f.name === name)).filter(Boolean);
  const rest = shown.filter((f) => !lead.includes(f));
  return [...new Set([...lead, ...rest])].slice(0, Math.max(limit, lead.length));
}
